import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Sparkles, Lock } from "lucide-react";
import { Immersive, immersiveSession } from "../lib/immersive";
import ExperienceEngine from "../components/immersive/ExperienceEngine";

export default function ImmersiveExperience() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const session = immersiveSession();

  useEffect(() => {
    setData(null); setErr(null);
    Immersive.experience(slug)
      .then((d) => {
        setData(d);
        Immersive.event("experience_started", { experience: slug, session_id: session });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load experience"));
  }, [slug, session]);

  if (err) {
    return (
      <div className="mythos-ink-bg min-h-screen grid place-items-center p-10">
        <div className="text-center">
          <div className="text-[#F4EFE6]/70">{err}</div>
          <Link to="/immersive" className="mt-4 inline-flex items-center gap-1 mythos-gold hover:underline">← Back to Immersive Lab</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mythos-ink-bg min-h-screen grid place-items-center">
        <Loader2 className="animate-spin mythos-gold" size={28} />
      </div>
    );
  }

  // Preview experience (not yet shipped)
  if (data.preview) {
    return <PreviewScreen data={data} navigate={navigate} />;
  }

  // Full experience
  return (
    <ExperienceEngine
      meta={data.meta}
      stages={data.stages}
      assessment={data.assessment}
      accent={data.meta.cover.accent}
      accentDark={data.meta.cover.accent_dark}
    />
  );
}

function PreviewScreen({ data, navigate }) {
  const { meta, trailer } = data;
  const accent = meta.cover.accent;
  const accentDark = meta.cover.accent_dark;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % trailer.scenes.length), 3200);
    return () => clearInterval(t);
  }, [trailer.scenes.length]);

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg,#07090D 0%, #0E0F12 100%)" }} data-testid={`immersive-preview-${meta.slug}`}>
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-20">
        <Link to="/immersive" className="text-[11px] tracking-[0.22em] uppercase mythos-gold font-bold hover:opacity-80">← Immersive Lab</Link>
        <div className="mt-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: `${accent}22`, border: `1px solid ${accent}` }}>
            <Lock size={16} style={{ color: accent }} />
          </div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: accent }}>Preview · {meta.subject}</div>
        </div>
        <h1 className="font-mythos text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#F4EFE6] mt-3">{meta.title}</h1>
        <p className="font-mythos text-lg md:text-xl mt-4" style={{ color: accent }}>{meta.tagline}</p>
        <p className="text-[#F4EFE6]/70 mt-4 max-w-2xl">{meta.hook}</p>

        <div className="mt-10 rounded-2xl border overflow-hidden" style={{ background: accentDark, borderColor: `${accent}44` }}>
          <div className="relative" style={{ aspectRatio: "16/9", background: `radial-gradient(circle at 30% 20%, ${accent}22 0%, #050810 70%)` }}>
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 grid place-items-center px-10 text-center"
            >
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase font-bold mb-5" style={{ color: accent }}>
                  Scene 0{idx + 1} / 0{trailer.scenes.length}
                </div>
                <div className="font-mythos font-extrabold text-2xl md:text-4xl text-[#F4EFE6] leading-tight max-w-3xl">
                  {trailer.scenes[idx]}
                </div>
              </div>
            </motion.div>
            {/* progress rail */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2A3349]">
              <motion.div
                key={idx}
                initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 3.2, ease: "linear" }}
                className="h-full" style={{ background: accent }}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-2xl p-6 border" style={{ background: "#0A0D15", borderColor: `${accent}55` }}>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2" style={{ color: accent }}>Status</div>
          <div className="text-[#F4EFE6]/90 text-sm leading-relaxed">{trailer.status_line}</div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/xr/partners" className="rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 text-[#0E0F12]" style={{ background: accent }}>
              Request an early demo <ArrowRight size={14} />
            </Link>
            <button onClick={() => navigate("/immersive/data-packet")} className="rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 border transition" style={{ color: accent, borderColor: accent }} data-testid="preview-try-flagship">
              Try the live flagship <Sparkles size={14} />
            </button>
          </div>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {meta.capabilities.map((c) => (
            <div key={c} className="rounded-xl p-4 border" style={{ background: accentDark, borderColor: `${accent}33` }}>
              <div className="text-xs text-[#F4EFE6]/80">{c}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
